import { Link, createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import * as React from 'react'
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  KeyRound,
  Plus,
  Users,
} from 'lucide-react'
import { toast } from 'sonner'
import { TeamManagementDialog } from '../-components/TeamManagementDialog'
import { SpouseDialog } from '../-components/SpouseDialog'
import { HelpersManagementDialog } from '../-components/HelpersManagementDialog'
import { ClientPageHeader } from './-components/ClientPageHeader'
import { AnnualReviewCard } from './-components/AnnualReviewCard'
import { ClientInfoCard } from './-components/ClientInfoCard'
import { ClientAccountsCard } from './-components/ClientAccountsCard'
import { DefaultTaxMultiplierButton } from './-components/DefaultTaxMultiplierButton'
import { EmptySpouseCard } from './-components/EmptySpouseCard'
import { FinalEscapePlanCard } from './-components/FinalEscapePlanCard'
import { ManagementTeamSection } from './-components/ManagementTeamSection'
import { IntegrationExperienceCard } from './-components/IntegrationExperienceCard'
import { RothRadarCard } from './-components/RothRadarCard'
import { SectionNav } from './-components/SectionNav'
import { ClientFilesSection } from './-components/files/ClientFilesSection'
import { FilePreviewDialog } from './-components/files/FilePreviewDialog'
import { JourneySection } from './-components/JourneySection'
import type { ClientFileItem } from '@/types/clientFile.types'
import type { Client } from '@/types/client.types'
import type { Forecast } from '@/types/forecast.types'
import type { ForecastLabel } from '@/types/forecastLabel.types'
import type { ClientHelper } from '@/types/clientHelper.types'
import type { InviteStatus } from '@/components/data-gathering/portal-access-shared'
import { ClientAccessPopover } from '@/components/data-gathering/ClientAccessPopover'
import { statusDotClass } from '@/components/data-gathering/portal-access-shared'
import { RequireClientTeam } from '@/components/permissions/RequireClientTeam'
import { PageTitle } from '@/components/PageTitle'
import { cn } from '@/lib/utils'
import { getForecastLabels } from '@/lib/forecast-utils'
import { getFileDownloadUrlImperative } from '@/hooks/repositories/useClientFilesRepository'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import {
  useClientAnnualReviews,
  useClientById,
  useToggleFavorite,
  useUpdateClient,
  useUpdateClientTeam,
} from '@/hooks/repositories/useClientsRepository'
import {
  useActiveEscapePlan,
  useClientForecasts,
  useDeactivateEscapePlan,
  useDeleteForecast,
  useDuplicateForecast,
} from '@/hooks/repositories/useForecastsRepository'
import { useSetForecastLabels } from '@/hooks/repositories/useForecastLabelsRepository'
import { useAuth } from '@/contexts/AuthContext'
import { ActivateEscapePlanDialog } from '@/components/escape-plan/ActivateEscapePlanDialog'
import { PreviewEscapePlanDialog } from '@/components/escape-plan/PreviewEscapePlanDialog'
import { ForecastWizard } from '@/components/forecast/ForecastWizard'
import { ForecastsTable } from '@/components/forecast/ForecastsTable'
import { NewForecastSplitButton } from '@/components/forecast/forecast-groups/NewForecastSplitButton'
import { LegacyForecastsTable } from '@/components/forecast/legacy/LegacyForecastsTable'
import { PublishForecastDialog } from '@/components/forecast/PublishForecastDialog'
import { usePublishForecastWithReport } from '@/hooks/use-cases/usePublishForecastWithReport'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Fade } from '@/components/animate-ui/primitives/effects/fade'
import { buttonVariants } from '@/components/animate-ui/components/buttons/button'
import { DataGatheringSection } from '@/components/data-gathering/DataGatheringSection'
import {
  useDgFormByClient,
  usePortalStatus,
} from '@/hooks/repositories/useDataGatheringRepository'
import { useAnnualReviewReadiness } from '@/hooks/use-cases/useAnnualReviewReadiness'
import {
  useClientHelpers,
  useDeleteHelper,
} from '@/hooks/repositories/useClientHelpersRepository'
import { getHelperInitials } from '@/types/clientHelper.types'
import { UserAvatar } from '@/components/UserAvatar'
import { formatMutationError } from '@/lib/format-mutation-error'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/animate-ui/components/radix/alert-dialog'

const booleanSearchParam = z.preprocess((value) => {
  if (value === true || value === 'true' || value === '1') return true
  if (value === false || value === 'false' || value === '0') return false
  return undefined
}, z.boolean().optional())

const clientSearchSchema = z.object({
  section: z
    .enum([
      'journey',
      'client-info',
      'annual-review',
      'data-gathering',
      'forecasts',
      'integration-experience',
      'roth-radar',
      'files',
    ])
    .optional()
    .catch(undefined),
  rothRadarPositions: booleanSearchParam.optional().catch(false),
  /**
   * sc-1347 — one-shot instruction left by the `/annual-review/start`
   * external entry point (HubSpot/Front queue links), consumed exactly once
   * by `useAnnualReviewEntryAction` (wired into `AnnualReviewCard`) and then
   * stripped from the URL so a refresh doesn't re-trigger it.
   * `book-data-gathering` (stalled-alert Slack link) is consumed the same way
   * by `useDataGatheringBookEntryAction` in the journey tracker.
   */
  action: z
    .enum(['start-annual-review', 'book-data-gathering'])
    .optional()
    .catch(undefined),
  /**
   * Open one file straight into the preview dialog. Left by an external link —
   * the HubSpot ticket card's file rows — and consumed exactly once by
   * `useClientFileEntryAction`, which then strips both params.
   *
   * `sourceType` is not optional decoration: a file id is unique only within
   * its own source table, so the id alone does not identify a file.
   */
  file: z.string().optional().catch(undefined),
  sourceType: z
    .enum([
      'client_report',
      'forecast_file',
      'dg_form_attachment',
      'client_upload',
      'library_file',
    ])
    .optional()
    .catch(undefined),
})

export const Route = createFileRoute('/_app/clients/$clientId/')({
  component: RouteComponent,
  validateSearch: clientSearchSchema,
})

function RouteComponent() {
  const { clientId } = Route.useParams()
  const { section, rothRadarPositions } = Route.useSearch()
  const { user } = useAuth()
  // Read once here (the route component) and threaded down to
  // `FinalEscapePlanCard` below.
  const { annualReviews, taxReturnsFolder } = useFeatureFlags()
  const { data: client, isLoading } = useClientById(clientId)
  // SC-811 Part 2 / sc-1034 Phase 2: returning-client summary feeds the
  // Annual Review readiness below (SectionNav attention dots). Only fetched
  // when the flag is on - React Query dedupes with the identical call
  // inside `AnnualReviewCard`, so this adds no extra request.
  const { data: annualReviewSummary } = useClientAnnualReviews(
    annualReviews ? clientId : undefined,
  )
  const { data: clientForecasts = [], isLoading: isLoadingForecasts } =
    useClientForecasts(clientId)
  const { data: activeDgForm } = useDgFormByClient(clientId)
  // sc-1034 Phase 2: same readiness data behind `AnnualReviewCard`'s stepper,
  // reused here to drive the SectionNav "needs action" dots (Annual Review /
  // Data Gathering / Files) instead of re-deriving the same signals twice.
  const readiness = useAnnualReviewReadiness(clientId, annualReviewSummary)
  // "No planner assigned" is the same condition that renders "Unassigned" on
  // the Management Team planner row (`client.planner` is null). Derived here,
  // where the SectionNav dots are wired, so the Client Information tab can
  // carry the same "needs action" nudge as the readiness-driven ones below.
  const plannerMissing = Boolean(client && !client.planner)
  const attentionSectionIds = React.useMemo(() => {
    const ids = new Set<string>()
    // Independent of annual-review readiness: flag the Client Information tab
    // as soon as the client record shows no planner assigned.
    if (plannerMissing) ids.add('section-client-info')
    if (readiness.isLoading) return ids
    if (annualReviews && readiness.readyCount < readiness.totalSteps) {
      ids.add('section-annual-review')
    }
    for (const step of readiness.steps) {
      if (step.status === 'done') continue
      if (step.id === 'data-gathering') ids.add('section-data-gathering')
      if (step.id === 'files') ids.add('section-files')
    }
    return ids
  }, [
    plannerMissing,
    annualReviews,
    readiness.isLoading,
    readiness.readyCount,
    readiness.totalSteps,
    readiness.steps,
  ])
  const { data: activeEscapePlan, isLoading: isLoadingEscapePlan } =
    useActiveEscapePlan(clientId)
  const deactivateEscapePlan = useDeactivateEscapePlan(clientId)
  const toggleFavorite = useToggleFavorite()
  const updateClient = useUpdateClient()
  const updateTeam = useUpdateClientTeam()

  const [teamDialogOpen, setTeamDialogOpen] = React.useState(false)
  const [shouldAnimate, setShouldAnimate] = React.useState(false)
  const [wasFavorited, setWasFavorited] = React.useState<boolean | undefined>(
    undefined,
  )

  const handleToggleFavorite = () => {
    if (!user?.databaseId || !client) return
    // Store state before toggling
    setWasFavorited(client.isFavorite)
    setShouldAnimate(true)
    setTimeout(() => {
      setShouldAnimate(false)
      setWasFavorited(undefined)
    }, 400)
    // Trigger mutation
    toggleFavorite.mutate({
      userId: user.databaseId,
      clientId: client.id,
      isFavorite: !client.isFavorite,
    })
  }

  if (isLoading) {
    return <ClientDetailSkeleton />
  }

  if (!client) {
    return (
      <div className="py-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Client not found</h1>
          <p className="text-muted-foreground mt-2">
            The client you're looking for doesn't exist.
          </p>
          <Button asChild className="mt-4">
            <Link to="/clients">Back to Clients</Link>
          </Button>
        </div>
      </div>
    )
  }

  const hasPresentableForecast = clientForecasts.some((forecast) =>
    ['PUBLISHED', 'ACTIVE', 'COMPLETED'].includes(forecast.status),
  )
  // sc-1145: any forecast at all, DRAFT/ARCHIVED included - drives the
  // journey tracker's Plan Creation "in motion" (warning) tone. See the
  // TYLER QUESTION in useClientJourneyTracker.ts's doc header.
  const hasAnyForecast = clientForecasts.length > 0
  const hasEscapePlanCreated = clientForecasts.some(
    (forecast) =>
      Boolean(forecast.escapePlanActivatedAt) || forecast.isActiveEscapePlan,
  )
  const showRothRadar = Boolean(client.isRothRadarEntitled)
  // sc-1266: visibility only; `taxReturnsFolder` treats undefined as off
  // (LD not yet initialised). Independent of `isRothRadarEntitled`: the card
  // itself disables the Offer control with a reason when the client isn't
  // entitled, rather than hiding entirely, so staff can see why.
  const showIntegrationExperience = Boolean(taxReturnsFolder)

  return (
    <div className="py-4 space-y-4 overflow-visible">
      <PageTitle title={`${client.firstName} ${client.lastName}`} />
      {/* Back Button */}
      <Fade inView inViewOnce delay={0}>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/clients">
            <ArrowLeft className="size-4 mr-2" />
            Back to Clients
          </Link>
        </Button>
      </Fade>

      {/* Client hero + section nav - ONE sticky element below AppHeader, and
          deliberately ONE `backdrop-blur` rather than two stacked ones.
          `backdrop-filter` only samples the backdrop within its own element's
          bounds, so two adjacent blurred bars each blur their own slice and a
          bright element scrolling past the boundary resolves differently on
          either side of it - a visible seam. Blurring the whole block once
          removes the boundary, and also closes the `space-y-4` gap that used
          to open between the two while they settled into their sticky spots. */}
      <div
        id="client-sticky-header"
        className="sticky top-16 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 -mx-4 px-4"
      >
        <div className="py-3">
          <ClientPageHeader
            client={client}
            onToggleFavorite={handleToggleFavorite}
            shouldAnimate={shouldAnimate}
            wasFavorited={wasFavorited}
          />
        </div>

        <SectionNav
          initialSectionId={section ? `section-${section}` : null}
          attentionSectionIds={attentionSectionIds}
          showAnnualReview={Boolean(annualReviews)}
          showRothRadar={showRothRadar}
          showIntegrationExperience={showIntegrationExperience}
        />
      </div>

      {/* Journey Section - first content section */}
      <div id="section-journey" className="pt-2">
        <Fade inView inViewOnce delay={100}>
          <JourneySection
            client={client}
            activeDgFormStatus={activeDgForm?.status ?? null}
            hasAnyForecast={hasAnyForecast}
            hasPresentableForecast={hasPresentableForecast}
            hasEscapePlanCreated={hasEscapePlanCreated}
            clientForecasts={clientForecasts}
            activeDgForm={activeDgForm ?? null}
          />
        </Fade>
      </div>

      {/* Sticky stacking cards */}
      <ClientSections
        client={client}
        onManageTeam={() => setTeamDialogOpen(true)}
      />

      {/* Data Gathering */}
      <div id="section-data-gathering" className="pt-2">
        <Fade inView inViewOnce delay={125}>
          <DataGatheringSection clientId={clientId} />
        </Fade>
      </div>

      {/* Escape Plan + Annual Review - two-column row (SC-811 Part 2): the EP
          card used to live inside the Forecasts grid, but with a long forecast
          list it floated too high in that section and read poorly next to an
          AR card that can show reviews going back years. Placing them side by
          side here keeps escape-plan status and review cadence in one glance.
          Gated behind the annualReviews LD flag: when off, this whole row
          (including AnnualReviewCard, entirely new AR work) is skipped and
          FinalEscapePlanCard reverts to its PRE-AR home beside the forecasts
          table inside ForecastsSection below.

          Fixed-row rework: both cards are now a fixed height regardless of
          state (`AR_CARD_GRID`/`EP_CARD_GRID` in `lib/ar-ep-slots.ts`), so
          the row no longer needs to stretch one card to match the other's
          content-driven height - `items-start` replaces `items-stretch` and
          the `flex flex-col` wrappers that used to do the stretching are
          gone. AnnualReviewCard still leads on the LEFT (col-span-2) with
          FinalEscapePlanCard beside it on the RIGHT (col-span-1). */}
      {annualReviews && (
        <div id="section-annual-review" className="pt-2">
          <Fade inView inViewOnce delay={150}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:items-start">
              <div className="lg:col-span-2">
                <AnnualReviewCard
                  client={client}
                  hasForecasts={isLoadingForecasts ? undefined : hasAnyForecast}
                />
              </div>
              <FinalEscapePlanCard
                clientId={client.id}
                escapePlan={activeEscapePlan ?? null}
                isLoading={isLoadingEscapePlan}
                onDeactivate={(forecastId) =>
                  deactivateEscapePlan.mutate(forecastId)
                }
                isDeactivating={deactivateEscapePlan.isPending}
              />
            </div>
          </Fade>
        </div>
      )}

      {/* Forecasts Section */}
      <div id="section-forecasts" className="pt-2">
        <Fade inView inViewOnce delay={200}>
          <ForecastsSection
            forecasts={clientForecasts}
            client={client}
            showEscapePlanCard={!annualReviews}
            escapePlan={activeEscapePlan ?? null}
            isLoadingEscapePlan={isLoadingEscapePlan}
            onDeactivateEscapePlan={(forecastId) =>
              deactivateEscapePlan.mutate(forecastId)
            }
            isDeactivatingEscapePlan={deactivateEscapePlan.isPending}
          />
        </Fade>
      </div>

      {/* Integration Experience Section (sc-1266), above RothRadar™ */}
      {showIntegrationExperience && (
        <div id="section-integration-experience" className="pt-2">
          <Fade inView inViewOnce delay={210}>
            <IntegrationExperienceCard key={client.id} client={client} />
          </Fade>
        </div>
      )}

      {/* RothRadar™ Section */}
      {showRothRadar && (
        <div id="section-roth-radar" className="pt-2">
          <Fade inView inViewOnce delay={225}>
            <RothRadarCard
              key={client.id}
              clientId={client.id}
              initialPositionsOpen={Boolean(rothRadarPositions)}
            />
          </Fade>
        </div>
      )}

      {/* Files Section */}
      <div id="section-files" className="pt-2">
        <Fade inView inViewOnce delay={250}>
          <ClientFilesSection client={client} clientId={clientId} />
        </Fade>
      </div>

      {/* Team Management Dialog */}
      <TeamManagementDialog
        client={client}
        open={teamDialogOpen}
        onOpenChange={setTeamDialogOpen}
        onSave={(savedClientId, data) => {
          updateClient.mutate({
            clientId: savedClientId,
            payload: {
              advisorId: data.advisorId,
              plannerId: data.plannerId,
              specialistId: data.specialistId,
            },
          })
          updateTeam.mutate({
            clientId: savedClientId,
            teamMemberIds: data.teamMemberIds,
          })
        }}
      />
    </div>
  )
}

// ============================================================================
// Component: Client Header
// ============================================================================
function ClientSections({
  client,
  onManageTeam,
}: {
  client: Client
  onManageTeam: () => void
}) {
  const updateClient = useUpdateClient()
  const deleteHelper = useDeleteHelper(client.id)
  const [spouseDialogOpen, setSpouseDialogOpen] = React.useState(false)
  const [manageGuestsOpen, setManageGuestsOpen] = React.useState(false)
  const [removeSpouseOpen, setRemoveSpouseOpen] = React.useState(false)

  const hasCoClient = !!(client.coclientFirstName || client.coclientLastName)

  // The spouse can only be added as a guest once. The SpouseDialog checkbox
  // is hidden after a SPOUSE-type helper exists to prevent duplicates.
  const { data: helpers = [] } = useClientHelpers(client.id)
  const spouseGuests = helpers.filter((h) => h.type === 'SPOUSE')
  const spouseAlreadyHelper = spouseGuests.length > 0

  // Active DG form - used to thread formId into the ClientAccessPopover pill
  const { data: activeDgForm } = useDgFormByClient(client.id)

  // Portal invite status - shared React Query cache with ClientAccessPopover,
  // reused here so the Client Access trigger button can show the status dot
  // without re-deriving it separately.
  const { data: portalStatus } = usePortalStatus(client.id)
  const hasInvite = portalStatus?.portal.hasInvite ?? false
  const inviteStatus = portalStatus?.portal.latestInvite?.status as InviteStatus

  const clearCoClient = () => {
    updateClient.mutate({
      clientId: client.id,
      payload: {
        coclientFirstName: null,
        coclientLastName: null,
        coclientEmail: null,
        coclientDOB: null,
        coclientRetirementAge: null,
        coclientHorizon: 90,
      },
    })
  }

  // SC-722: removing the spouse also removes any SPOUSE guest login(s) so the
  // co-client metadata and the portal login stay in sync. Delete the guest(s)
  // first (revokes the Keycloak login); only clear the co-client once that
  // succeeds so we never strip the metadata behind a still-live login.
  const confirmRemoveSpouse = async () => {
    try {
      for (const guest of spouseGuests) {
        await deleteHelper.mutateAsync(guest.id)
      }
    } catch (err) {
      toast.error(
        formatMutationError("Failed to remove the spouse's guest login", err),
      )
      return
    }
    clearCoClient()
    setRemoveSpouseOpen(false)
  }

  return (
    <>
      <div className="space-y-4">
        {/* Client Details */}
        <div id="section-client-info" className="pt-2">
          <Fade inView inViewOnce delay={50}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
              {/* Left Side - Client Info */}
              <div className="lg:col-span-2 flex flex-col">
                <Card className="shadow-lg flex-1">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users className="size-5 text-muted-foreground" />
                        <CardTitle>Client Information</CardTitle>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setManageGuestsOpen(true)}
                        className="h-8 gap-2 px-3"
                      >
                        <Users className="size-4" />
                        Manage Guests
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ClientInfoCard
                        firstName={client.firstName}
                        lastName={client.lastName}
                        email={client.email}
                        dob={client.clientDOB}
                        role="Primary"
                        clientId={client.id}
                        phoneNumber={client.phoneNumber}
                        state={client.state}
                        portalControls={
                          <RequireClientTeam clientId={client.id}>
                            <ClientAccessPopover
                              clientId={client.id}
                              dgContext="onboarding"
                              formId={activeDgForm?.id}
                              portalAccessScope={client.portalAccessScope}
                              rothRadarEntitled={Boolean(
                                client.isRothRadarEntitled,
                              )}
                              showPortalLinkAction
                              align="end"
                              renderTrigger={({ open }) => (
                                <button
                                  type="button"
                                  className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-muted px-2.5 text-xs font-medium text-muted-foreground hover:bg-muted/80"
                                  aria-haspopup="dialog"
                                  aria-expanded={open}
                                  aria-label="Client portal access options"
                                >
                                  <KeyRound size={14} className="shrink-0" />
                                  <span className="hidden sm:inline">
                                    Client Access
                                  </span>
                                  <span
                                    className={cn(
                                      'size-2 shrink-0 rounded-full',
                                      statusDotClass(hasInvite, inviteStatus),
                                    )}
                                    aria-hidden
                                  />
                                  <ChevronDown
                                    size={13}
                                    className={cn(
                                      'shrink-0 text-muted-foreground transition-transform duration-150',
                                      open && 'rotate-180',
                                    )}
                                  />
                                </button>
                              )}
                            />
                          </RequireClientTeam>
                        }
                      />
                      {hasCoClient ? (
                        <ClientInfoCard
                          firstName={client.coclientFirstName ?? ''}
                          lastName={client.coclientLastName ?? ''}
                          email={client.coclientEmail ?? undefined}
                          dob={client.coclientDOB}
                          role="Spouse"
                          onEdit={() => setSpouseDialogOpen(true)}
                          onRemove={() => setRemoveSpouseOpen(true)}
                        />
                      ) : (
                        <EmptySpouseCard
                          onAddSpouse={() => setSpouseDialogOpen(true)}
                        />
                      )}
                    </div>
                    <GuestsStrip
                      helpers={helpers}
                      onManageGuests={() => setManageGuestsOpen(true)}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Right Side - Management Team */}
              <div className="lg:col-span-1 flex flex-col gap-4">
                <ManagementTeamSection
                  client={client}
                  onManageTeam={onManageTeam}
                  compact
                />
              </div>
            </div>

            {/* Full width, below the people and the assignments: money is its
                own subject, and giving it the whole row stops the left column
                running to twice the height of the right. */}
            <div className="mt-4">
              <ClientAccountsCard
                clientId={client.id}
                clientName={[client.firstName, client.lastName]
                  .filter(Boolean)
                  .join(' ')}
              />
            </div>
          </Fade>
        </div>
      </div>

      <SpouseDialog
        client={client}
        open={spouseDialogOpen}
        onOpenChange={setSpouseDialogOpen}
        spouseAlreadyGuest={spouseAlreadyHelper}
        spouseGuest={spouseGuests[0]}
      />

      <HelpersManagementDialog
        client={client}
        open={manageGuestsOpen}
        onOpenChange={setManageGuestsOpen}
      />

      <AlertDialog open={removeSpouseOpen} onOpenChange={setRemoveSpouseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove spouse?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the spouse / co-client details for{' '}
              <strong>
                {client.firstName} {client.lastName}
              </strong>
              .
              {spouseAlreadyHelper && (
                <>
                  {' '}
                  It will also remove their guest portal login, revoking their
                  access.
                </>
              )}{' '}
              Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteHelper.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void confirmRemoveSpouse()
              }}
              disabled={deleteHelper.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteHelper.isPending ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ============================================================================
// Component: Guests Strip
// ============================================================================
function GuestsStrip({
  helpers,
  onManageGuests,
}: {
  helpers: Array<ClientHelper>
  onManageGuests: () => void
}) {
  const visibleHelpers = helpers.slice(0, 5)
  const overflowCount = helpers.length - visibleHelpers.length

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onManageGuests}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onManageGuests()
        }
      }}
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/50 bg-background/30 px-3 py-2.5 cursor-pointer transition-colors hover:bg-muted/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Guests
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {helpers.length === 0
            ? 'No guests yet'
            : `${helpers.length} guest portal login${helpers.length === 1 ? '' : 's'}`}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {helpers.length > 0 && (
          <div className="flex items-center -space-x-2">
            {visibleHelpers.map((helper) => (
              <UserAvatar
                key={helper.id}
                initials={getHelperInitials(helper.firstName, helper.lastName)}
                size="xs"
                className="ring-2 ring-background"
              />
            ))}
            {overflowCount > 0 && (
              <div className="flex size-6 items-center justify-center rounded-full border border-border/60 bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-background">
                +{overflowCount}
              </div>
            )}
          </div>
        )}
        <ChevronRight className="size-4 text-muted-foreground/50" />
      </div>
    </div>
  )
}

// ============================================================================
// Component: Forecasts Section
// ============================================================================
function ForecastsSection({
  forecasts,
  client,
  showEscapePlanCard = false,
  escapePlan = null,
  isLoadingEscapePlan = false,
  onDeactivateEscapePlan,
  isDeactivatingEscapePlan = false,
}: {
  forecasts: Array<Forecast>
  client: Client
  /** SC-811 Part 2: when the annualReviews flag is OFF, the escape-plan card
   * moves back beside the forecasts table here (its PRE-AR home) instead of
   * living in the full-width `section-annual-review` row. */
  showEscapePlanCard?: boolean
  escapePlan?: Forecast | null
  isLoadingEscapePlan?: boolean
  onDeactivateEscapePlan?: (forecastId: string) => void
  isDeactivatingEscapePlan?: boolean
}) {
  const { user } = useAuth()
  // sc-608 — the only thing deciding which Forecasts card renders below:
  // the current forecast-group card (with the recommendations wizard entry
  // points) while ON, or `LegacyForecastsTable` (a frozen pre-sc-608 copy —
  // see its own doc comment) while OFF, so a client with the flag off sees
  // exactly the old flat table.
  const { interactiveEscapePlan } = useFeatureFlags()
  const [wizardOpen, setWizardOpen] = React.useState(false)
  const [selectedForecast, setSelectedForecast] = React.useState<
    Forecast | undefined
  >(undefined)
  const [activateTarget, setActivateTarget] = React.useState<Forecast | null>(
    null,
  )
  // sc-1074: publish a draft directly from the list, without opening the
  // wizard. The hook lives here (page level) rather than per-row, so
  // onOpenEditor has to look the forecast back up by id.
  const [publishTarget, setPublishTarget] = React.useState<Forecast | null>(
    null,
  )
  const publishWithReport = usePublishForecastWithReport(client.id, {
    onOpenEditor: (id) => {
      const target = forecasts.find((f) => f.id === id)
      if (target) handleEditDraft(target)
    },
  })
  const [previewEscapePlanTarget, setPreviewEscapePlanTarget] =
    React.useState<Forecast | null>(null)

  // Report preview state
  const [previewFile, setPreviewFile] = React.useState<ClientFileItem | null>(
    null,
  )
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null)
  const [isLoadingPreviewUrl, setIsLoadingPreviewUrl] = React.useState(false)

  // Duplicate forecast mutation
  const duplicateMutation = useDuplicateForecast(client.id)
  const duplicatingForecastId = duplicateMutation.isPending
    ? duplicateMutation.variables
    : null
  const setForecastLabelsMutation = useSetForecastLabels()
  // sc-XXXX: when the source forecast has labels, confirm whether to copy
  // them before duplicating — the backend's duplicate endpoint does not copy
  // labels, so this frontend orchestrates it as a follow-up PUT.
  const [duplicateLabelsPrompt, setDuplicateLabelsPrompt] = React.useState<{
    forecast: Forecast
    sourceLabels: Array<ForecastLabel>
  } | null>(null)

  // Delete forecast mutation
  const deleteMutation = useDeleteForecast(client.id)
  const deletingForecastId = deleteMutation.isPending
    ? deleteMutation.variables
    : null

  const handleEditDraft = (forecast: Forecast) => {
    setSelectedForecast(forecast)
    setWizardOpen(true)
  }

  const publishTargetHasReportPdf = Boolean(
    publishTarget?.clientReports?.some(
      (r) => r.reportType === 'ROTHOLOGY_REPORT',
    ),
  )

  const handleConfirmPublish = async () => {
    if (!publishTarget) return
    const success = await publishWithReport.publish({
      forecastId: publishTarget.id,
      hasReportPdf: publishTargetHasReportPdf,
    })
    if (success) setPublishTarget(null)
  }

  const handleNewForecastClick = () => {
    setWizardOpen(true)
  }

  const handleCloseWizard = (open: boolean) => {
    setWizardOpen(open)
    if (!open) {
      setSelectedForecast(undefined)
    }
  }

  // Runs the actual duplicate + (optionally) copies the source's labels onto
  // the new forecast, then opens the wizard on the copy. `copyLabels` is only
  // ever true when the caller already confirmed via the prompt below.
  const finishDuplicateForecast = async (
    sourceForecast: Forecast,
    copyLabels: boolean,
  ) => {
    try {
      const newForecast = await duplicateMutation.mutateAsync(sourceForecast.id)
      if (copyLabels) {
        const sourceLabelIds = getForecastLabels(sourceForecast).map(
          (l) => l.id,
        )
        try {
          const copiedLabels = await setForecastLabelsMutation.mutateAsync({
            forecastId: newForecast.id,
            labelIds: sourceLabelIds,
          })
          // So the wizard (which reads existingForecast.labelAssignments via
          // mapForecastToBasicsData) shows the copied labels immediately,
          // without waiting on the invalidated query to refetch.
          newForecast.labelAssignments = copiedLabels.map((label) => ({
            label,
          }))
        } catch (e) {
          // useSetForecastLabels already toasts the failure — the duplicate
          // itself still succeeded, so proceed into the wizard regardless.
          console.error('Failed to copy labels to the duplicated forecast:', e)
        }
      }
      setSelectedForecast(newForecast)
      setWizardOpen(true)
    } catch {
      // Error toast is handled by the mutation's onError callback
    }
  }

  const handleDuplicateForecast = (forecast: Forecast) => {
    const sourceLabels = getForecastLabels(forecast)
    if (sourceLabels.length > 0) {
      setDuplicateLabelsPrompt({ forecast, sourceLabels })
      return
    }
    void finishDuplicateForecast(forecast, false)
  }

  const handlePreviewReport = async (forecast: Forecast) => {
    // The eye action always previews the Rothology Report specifically —
    // `clientReports` is ordered most-recent-first, so on a forecast with an
    // active escape plan `clientReports?.[0]` would actually be the
    // ESCAPE_PLAN report, not the Rothology one (sc-1100).
    const report = forecast.clientReports?.find(
      (r) => r.reportType === 'ROTHOLOGY_REPORT',
    )
    if (!report) return

    const filename = `RR - ${forecast.title}.pdf`

    const fileItem: ClientFileItem = {
      id: report.id,
      category: 'ROTHOLOGY_REPORT',
      filename,
      contentType: 'application/pdf',
      fileSize: null,
      createdAt: forecast.createdAt,
      forecastId: forecast.id,
      forecastTitle: forecast.title,
      uploadedBy: null,
      sourceType: 'client_report',
      sourceId: report.id,
      folderPath: null,
      hasAnnotations: report.hasAnnotations,
    }

    setPreviewFile(fileItem)
    setPreviewUrl(null)
    setIsLoadingPreviewUrl(true)
    try {
      // 'inline' so the report preview renders in-browser instead of
      // downloading on open (sc-1081).
      const url = await getFileDownloadUrlImperative(
        client.id,
        report.id,
        'client_report',
        filename,
        'inline',
      )
      setPreviewUrl(url)
    } finally {
      setIsLoadingPreviewUrl(false)
    }
  }

  // sc-608 round 6 — see the `forecastGroupActionSlot` comment in the header
  // below. A state ref (not `useRef`) so the portal re-renders once the node
  // exists.
  const [forecastsToolbarSlot, setForecastsToolbarSlot] =
    React.useState<HTMLDivElement | null>(null)
  // sc-608 (C2 header) — mount point for the chevron half of "New Forecast"
  // (`NewForecastSplitButton`): same portal pattern, same reason — creating a
  // forecast group is state `ForecastsTable` owns, not this route.
  const [newForecastOptionsSlot, setNewForecastOptionsSlot] =
    React.useState<HTMLDivElement | null>(null)
  // sc-608 Forecasts card visual-fidelity pass — mount point for
  // `ForecastsTable`'s "{n} forecasts · {m} forecast groups" subtitle
  // (`R8Card.dc.html`), same portal pattern as `forecastsToolbarSlot` above:
  // the counts depend on data `ForecastsTable` owns (filters, the stored
  // forecast groups), but the "Forecasts" title sits in this route's
  // `CardHeader`.
  const [forecastsSubtitleSlot, setForecastsSubtitleSlot] =
    React.useState<HTMLDivElement | null>(null)

  const forecastsCard = (
    <Card className={cn('shadow-lg', showEscapePlanCard && 'flex-1')}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <CardTitle>Forecasts</CardTitle>
            <div ref={setForecastsSubtitleSlot} className="contents" />
          </div>
          {/* Wraps rather than overflowing the card: with the portalled
              controls (the New Forecast chevron, the Recommendations menu)
              this row is wider than a narrow card (the 2/3-width layout) can
              hold. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Beside the action it affects: the multiplier only ever changes
                the NEXT forecast, so this is the moment it matters. */}
            <DefaultTaxMultiplierButton client={client} />
            {/* sc-608 (C2 header) — with the flag ON, "New Forecast" is a
                split button whose chevron ("New Forecast Group") is
                portalled into its own mount point. OFF: the plain button,
                exactly as before. */}
            {interactiveEscapePlan ? (
              <NewForecastSplitButton
                onClick={handleNewForecastClick}
                optionsSlotRef={setNewForecastOptionsSlot}
              />
            ) : (
              <Button size="sm" onClick={handleNewForecastClick}>
                <Plus className="size-4 mr-2" />
                New Forecast
              </Button>
            )}
            {/* sc-608 round 13 — mount point for the ONE recommendations
                control ("Build recommendations", or the "Recommendations"
                menu once a set exists), right after "New Forecast"
                (`R13OneButton`), whatever the number of forecast groups.
                `ForecastsTable` portals it in (and owns all of its state);
                `display: contents` keeps this wrapper out of the flex layout,
                so the control sits in this row itself. */}
            <div ref={setForecastsToolbarSlot} className="contents" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {forecasts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              No forecasts created yet for this client.
            </p>
            <Button onClick={() => setWizardOpen(true)}>
              <Plus className="size-4 mr-2" />
              Create First Forecast
            </Button>
          </div>
        ) : interactiveEscapePlan ? (
          <ForecastsTable
            forecasts={forecasts}
            clientId={client.id}
            onEditDraft={handleEditDraft}
            onPreviewReport={handlePreviewReport}
            onDuplicateForecast={handleDuplicateForecast}
            duplicatingForecastId={duplicatingForecastId}
            onDeleteForecast={(forecastId) => deleteMutation.mutate(forecastId)}
            deletingForecastId={deletingForecastId}
            onActivateEscapePlan={setActivateTarget}
            onPublishDraft={setPublishTarget}
            publishingForecastId={
              publishWithReport.isBusy
                ? publishWithReport.activeForecastId
                : null
            }
            onPreviewEscapePlan={setPreviewEscapePlanTarget}
            forecastGroupActionSlot={forecastsToolbarSlot}
            newForecastOptionsSlot={newForecastOptionsSlot}
            subtitleSlot={forecastsSubtitleSlot}
          />
        ) : (
          // sc-608 — `interactiveEscapePlan` OFF: the frozen pre-sc-608 table
          // (see `LegacyForecastsTable`'s doc comment). The header's subtitle
          // and toolbar portal slots above stay mounted but empty — nothing
          // portals into them while this branch renders, so they render as
          // invisible `display: contents` nodes, same as on main.
          <LegacyForecastsTable
            forecasts={forecasts}
            clientId={client.id}
            onEditDraft={handleEditDraft}
            onPreviewReport={handlePreviewReport}
            onDuplicateForecast={handleDuplicateForecast}
            duplicatingForecastId={duplicatingForecastId}
            onDeleteForecast={(forecastId) => deleteMutation.mutate(forecastId)}
            deletingForecastId={deletingForecastId}
            onActivateEscapePlan={setActivateTarget}
            onPublishDraft={setPublishTarget}
            publishingForecastId={
              publishWithReport.isBusy
                ? publishWithReport.activeForecastId
                : null
            }
            onPreviewEscapePlan={setPreviewEscapePlanTarget}
          />
        )}
      </CardContent>
    </Card>
  )

  return (
    <>
      {showEscapePlanCard ? (
        // Pre-AR layout (annualReviews flag OFF): escape-plan card sits
        // beside the forecasts table, same grid/wrapper structure this
        // section used before the SC-811 Part 2 move to the full-width
        // section-annual-review row.
        <div className="grid grid-cols-1 gap-4 items-stretch lg:grid-cols-3">
          <div className="lg:col-span-2 flex flex-col">{forecastsCard}</div>
          {/* Fixed-row rework: FinalEscapePlanCard is now a fixed 306px
              regardless of state, so it opts out of the row's own
              `items-stretch` with `self-start` rather than growing to match
              the (250-900px) Forecasts card beside it. */}
          <div className="lg:col-span-1 lg:self-start">
            <FinalEscapePlanCard
              clientId={client.id}
              escapePlan={escapePlan}
              isLoading={isLoadingEscapePlan}
              onDeactivate={onDeactivateEscapePlan}
              isDeactivating={isDeactivatingEscapePlan}
            />
          </div>
        </div>
      ) : (
        forecastsCard
      )}

      {/* Forecast Wizard Dialog */}
      <ForecastWizard
        open={wizardOpen}
        onOpenChange={handleCloseWizard}
        client={client}
        existingForecast={selectedForecast}
      />

      {/* Publish Draft Dialog (sc-1074) - publishing directly from the list */}
      <PublishForecastDialog
        open={!!publishTarget}
        onOpenChange={(open) => {
          if (!open) setPublishTarget(null)
        }}
        forecastTitle={publishTarget?.title ?? ''}
        hasReportPdf={publishTargetHasReportPdf}
        phase={publishWithReport.phase}
        progress={publishWithReport.progress}
        onConfirm={() => void handleConfirmPublish()}
      />

      {/* Activate Escape Plan Dialog */}
      {activateTarget && (
        <ActivateEscapePlanDialog
          open={!!activateTarget}
          onOpenChange={(open) => {
            if (!open) setActivateTarget(null)
          }}
          forecast={activateTarget}
          clientId={client.id}
        />
      )}

      {/* Preview Escape Plan Dialog */}
      {previewEscapePlanTarget && (
        <PreviewEscapePlanDialog
          open={!!previewEscapePlanTarget}
          onOpenChange={(open) => {
            if (!open) setPreviewEscapePlanTarget(null)
          }}
          forecast={previewEscapePlanTarget}
          clientId={client.id}
        />
      )}

      {/* Report Preview Dialog */}
      <FilePreviewDialog
        file={previewFile}
        url={previewUrl}
        isLoading={isLoadingPreviewUrl}
        onClose={() => {
          setPreviewFile(null)
          setPreviewUrl(null)
        }}
        onDownload={() => {
          if (previewUrl) {
            window.open(previewUrl, '_blank', 'noopener,noreferrer')
          }
        }}
        clientId={client.id}
        userName={user ? `${user.firstName} ${user.lastName}` : 'Guest'}
      />

      {/* Copy-labels-to-duplicate prompt. Dismiss (Escape/overlay) behaves
          like "Don't copy" — same convention as the wizard's default-tax-
          multiplier prompt: the duplicate still happens either way, only the
          label copy is skipped. */}
      <AlertDialog
        open={!!duplicateLabelsPrompt}
        onOpenChange={(open) => {
          if (open) return
          const target = duplicateLabelsPrompt?.forecast
          setDuplicateLabelsPrompt(null)
          if (target) void finishDuplicateForecast(target, false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Copy the {duplicateLabelsPrompt?.sourceLabels.length} label
              {duplicateLabelsPrompt &&
              duplicateLabelsPrompt.sourceLabels.length !== 1
                ? 's'
                : ''}{' '}
              to the new forecast?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">
                {duplicateLabelsPrompt?.forecast.title}
              </span>{' '}
              has label
              {duplicateLabelsPrompt &&
              duplicateLabelsPrompt.sourceLabels.length !== 1
                ? 's'
                : ''}{' '}
              applied. You can carry them over to the duplicate, or start it
              with no labels.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              className={buttonVariants({ variant: 'secondary' })}
              onClick={() => {
                const target = duplicateLabelsPrompt?.forecast
                setDuplicateLabelsPrompt(null)
                if (target) void finishDuplicateForecast(target, false)
              }}
            >
              Don&apos;t copy
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => {
                const target = duplicateLabelsPrompt?.forecast
                setDuplicateLabelsPrompt(null)
                if (target) void finishDuplicateForecast(target, true)
              }}
            >
              Copy labels
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// ============================================================================
// Component: Loading Skeleton
// ============================================================================
function ClientDetailSkeleton() {
  return (
    <div className="py-4 space-y-4">
      <Skeleton className="h-10 w-32" />
      <Skeleton className="h-48 w-full rounded-xl" />
      <Skeleton className="h-16 w-full" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    </div>
  )
}
